import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Phone, Bell, ChevronDown, User, Calendar, FileText, ChevronRight, Clock } from 'lucide-react';
import { Toast } from './Toast';
import { getRelativeTimeString } from '../utils/timeUtils';
import { useEscapeKey } from '../hooks/useEscapeKey';
type IssueType = 'Documents' | 'Inspections' | 'Pre-Shifts' | 'Toolbox Talk' | 'JHA' | 'PTP';
type GroupBy = 'type' | 'person' | 'date';
type ContactRole = 'Superintendent' | 'SSM' | 'Foreman';
interface Issue {
  id: string;
  type: IssueType;
  title: string;
  description: string;
  contractor: string;
  contactName: string;
  contactRole: ContactRole;
  contactPhone: string;
  date: string;
  priority: 'high' | 'medium' | 'low';
}
const MOCK_ISSUES: Issue[] = [{
  id: '1',
  type: 'Documents',
  title: '72 Expired Documents',
  description: 'Multiple permits and certifications have expired',
  contractor: 'Broadway Construction Group',
  contactName: 'John Smith',
  contactRole: 'Superintendent',
  contactPhone: '(555) 123-4567',
  date: '2024-01-15',
  priority: 'high'
}, {
  id: '2',
  type: 'Documents',
  title: '33 Documents Expiring Soon',
  description: 'Documents will expire within the next 30 days',
  contractor: 'AV Mechanic LLC',
  contactName: 'Sarah Johnson',
  contactRole: 'SSM',
  contactPhone: '(555) 234-5678',
  date: '2024-01-15',
  priority: 'medium'
}, {
  id: '3',
  type: 'Inspections',
  title: '4 Inspections In Progress',
  description: 'Weekly inspections not yet completed',
  contractor: 'Broadway Construction Group',
  contactName: 'Martin Gilrane',
  contactRole: 'SSM',
  contactPhone: '(555) 345-6789',
  date: '2024-01-15',
  priority: 'high'
}, {
  id: '4',
  type: 'Pre-Shifts',
  title: '69 Incomplete Pre-Shifts',
  description: 'Pre-shift reports from earlier this week',
  contractor: 'SteelCo Inc',
  contactName: 'David Chen',
  contactRole: 'Foreman',
  contactPhone: '(555) 456-7890',
  date: '2024-01-12',
  priority: 'high'
}, {
  id: '5',
  type: 'Toolbox Talk',
  title: '1 Incomplete Toolbox Talk',
  description: 'This week toolbox talk not completed',
  contractor: 'Clear Path Contracting LLC',
  contactName: 'James Rodriguez',
  contactRole: 'Foreman',
  contactPhone: '(555) 567-8901',
  date: '2024-01-15',
  priority: 'medium'
}, {
  id: '6',
  type: 'Toolbox Talk',
  title: '2 Incomplete Toolbox Talks',
  description: 'Previous week toolbox talks not completed',
  contractor: 'Broadway Construction Group',
  contactName: 'M. Elizabeth Lagana',
  contactRole: 'SSM',
  contactPhone: '(555) 678-9012',
  date: '2024-01-08',
  priority: 'low'
}, {
  id: '7',
  type: 'JHA',
  title: '2 Incomplete JHA This Week',
  description: 'Job Hazard Analysis forms not submitted',
  contractor: 'AV Mechanic LLC',
  contactName: 'Sarah Mitchell',
  contactRole: 'Superintendent',
  contactPhone: '(555) 789-0123',
  date: '2024-01-15',
  priority: 'high'
}, {
  id: '8',
  type: 'JHA',
  title: '3 Incomplete JHA Across Projects',
  description: 'Outstanding JHA forms from multiple sites',
  contractor: 'SteelCo Inc',
  contactName: 'Robert Williams',
  contactRole: 'Superintendent',
  contactPhone: '(555) 890-1234',
  date: '2024-01-10',
  priority: 'medium'
}, {
  id: '9',
  type: 'PTP',
  title: '3 Incomplete PTP Today',
  description: 'Pre-Task Plans not completed for today',
  contractor: 'Broadway Construction Group',
  contactName: 'John Smith',
  contactRole: 'Superintendent',
  contactPhone: '(555) 123-4567',
  date: '2024-01-15',
  priority: 'high'
}, {
  id: '10',
  type: 'PTP',
  title: '5 Incomplete PTP This Week',
  description: 'Outstanding Pre-Task Plans from this week',
  contractor: 'Clear Path Contracting LLC',
  contactName: 'James Rodriguez',
  contactRole: 'Foreman',
  contactPhone: '(555) 567-8901',
  date: '2024-01-14',
  priority: 'medium'
}];
interface IssuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterType?: IssueType | 'all';
}
export function IssuesModal({
  isOpen,
  onClose,
  filterType = 'all'
}: IssuesModalProps) {
  useEscapeKey(onClose, isOpen);
  const [groupBy, setGroupBy] = useState<GroupBy>('type');
  const [pokedIssues, setPokedIssues] = useState<Set<string>>(new Set());
  const [pokeTimestamps, setPokeTimestamps] = useState<Map<string, Date>>(new Map());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [, setTick] = useState(0);
  // Update relative time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => prev + 1);
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);
  const filteredIssues = filterType === 'all' ? MOCK_ISSUES : MOCK_ISSUES.filter(issue => issue.type === filterType);
  const handlePoke = (issueId: string, contactName: string) => {
    const now = new Date();
    setPokedIssues(prev => new Set(prev).add(issueId));
    setPokeTimestamps(prev => new Map(prev).set(issueId, now));
    setToastMessage(`Notification sent to ${contactName}`);
    setShowToast(true);
    // Reset poked state after animation
    setTimeout(() => {
      setPokedIssues(prev => {
        const newSet = new Set(prev);
        newSet.delete(issueId);
        return newSet;
      });
    }, 3000);
  };
  const handlePokeAll = () => {
    const now = new Date();
    const allContacts = [...new Set(filteredIssues.map(i => i.contactName))];
    filteredIssues.forEach(issue => {
      setPokedIssues(prev => new Set(prev).add(issue.id));
      setPokeTimestamps(prev => new Map(prev).set(issue.id, now));
    });
    setToastMessage(`Notifications sent to ${allContacts.length} people`);
    setShowToast(true);
    setTimeout(() => {
      setPokedIssues(new Set());
    }, 3000);
  };
  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };
  const groupedIssues = () => {
    if (groupBy === 'type') {
      return filteredIssues.reduce((acc, issue) => {
        if (!acc[issue.type]) acc[issue.type] = [];
        acc[issue.type].push(issue);
        return acc;
      }, {} as Record<string, Issue[]>);
    } else if (groupBy === 'person') {
      return filteredIssues.reduce((acc, issue) => {
        if (!acc[issue.contactName]) acc[issue.contactName] = [];
        acc[issue.contactName].push(issue);
        return acc;
      }, {} as Record<string, Issue[]>);
    } else {
      return filteredIssues.reduce((acc, issue) => {
        if (!acc[issue.date]) acc[issue.date] = [];
        acc[issue.date].push(issue);
        return acc;
      }, {} as Record<string, Issue[]>);
    }
  };
  const grouped = groupedIssues();
  if (!isOpen) return null;
  return <>
      <AnimatePresence data-id="element-789" data-mp="3gzgk2gketrg2">
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-790" data-mp="3gyoy7xgm782z">
          {/* Backdrop */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-791" data-mp="3cxd4pgume2pi" />

          {/* Modal */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5,
          bounce: 0.3
        }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" data-id="element-792" data-mp="1rks6ic0323gg">
            {/* Header */}
            <div className="p-6 border-b border-gray-200" data-id="element-793" data-mp="rrbrcnd3scqk">
              <div className="flex items-center justify-between mb-4" data-id="element-794" data-mp="rty0usjk12bf">
                <div data-id="element-795" data-mp="wpd9n62pg80z">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-796" data-mp="wt16ae3g5ajx">
                    {filterType === 'all' ? 'All Issues' : `${filterType} Issues`}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-797" data-mp="2d56dhoks43sj">
                    {filteredIssues.length} issue
                    {filteredIssues.length !== 1 ? 's' : ''} requiring attention
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-798" data-mp="3sp71h6kjut8m">
                  <button onClick={handlePokeAll} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors" data-id="element-799" data-mp="3nxfl1ov3y3w7">
                    <Bell size={16} data-id="element-800" data-mp="3e0131ktjd71s" />
                    Poke All
                  </button>
                  <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-801" data-mp="39wld17bz24y2">
                    <X size={20} data-id="element-802" data-mp="300s0hny96bga" />
                  </button>
                </div>
              </div>

              {/* Group By Controls */}
              <div className="flex items-center gap-3" data-id="element-803" data-mp="134u7hlaqfpe0">
                <span className="text-sm font-medium text-gray-700" data-id="element-804" data-mp="y9eyb6no6hnx">
                  Group by:
                </span>
                <div className="flex gap-2" data-id="element-805" data-mp="1ah27d2jldrgp">
                  <button onClick={() => setGroupBy('type')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${groupBy === 'type' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-806" data-mp="15kbraf7sevva">
                    <FileText size={14} className="inline mr-1" data-id="element-807" data-mp="rs5mfpa34bb5" />
                    Type
                  </button>
                  <button onClick={() => setGroupBy('person')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${groupBy === 'person' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-808" data-mp="mu3t8biy2vly">
                    <User size={14} className="inline mr-1" data-id="element-809" data-mp="83mtcwlykdr6" />
                    Person
                  </button>
                  <button onClick={() => setGroupBy('date')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${groupBy === 'date' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-810" data-mp="41rpplgk1oa8">
                    <Calendar size={14} className="inline mr-1" data-id="element-811" data-mp="3g75mdswz4rk5" />
                    Date
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6" data-id="element-812" data-mp="cqfc31uczscn">
              {Object.entries(grouped).map(([groupKey, issues]) => {
              const isCollapsed = collapsedGroups.has(groupKey);
              return <div key={groupKey} className="mb-6 last:mb-0" data-id="element-813" data-mp="2qy6slj0rgh7">
                    <button onClick={() => toggleGroup(groupKey)} className="w-full text-left mb-3 flex items-center gap-2 hover:bg-gray-50 p-2 rounded-lg transition-colors" data-id="element-814" data-mp="7phr6hhysk7g">
                      <motion.div animate={{
                    rotate: isCollapsed ? 0 : 90
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-815" data-mp="2sefb7lwwacax">
                        <ChevronRight size={18} className="text-gray-500" data-id="element-816" data-mp="p6f15harckml" />
                      </motion.div>
                      {groupBy === 'type' && <FileText size={18} data-id="element-817" data-mp="k72asmkogvf9" />}
                      {groupBy === 'person' && <User size={18} data-id="element-818" data-mp="fbd7c85jeudo" />}
                      {groupBy === 'date' && <Calendar size={18} data-id="element-819" data-mp="b7yshktc4k1t" />}
                      <h3 className="text-lg font-bold text-gray-900 flex-1" data-id="element-820" data-mp="bab99uncwoda">
                        {groupKey}
                      </h3>
                      <span className="text-sm font-normal text-gray-500" data-id="element-821" data-mp="1akdzqqz25c5">
                        ({issues.length})
                      </span>
                    </button>

                    <AnimatePresence initial={false} data-id="element-822" data-mp="20ksj65ity5up">
                      {!isCollapsed && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-823" data-mp="1vm8ye8fz303x">
                          <div className="space-y-3 pl-8" data-id="element-824" data-mp="1z5ptr9gbg0g8">
                            {issues.map(issue => {
                        const isPoked = pokedIssues.has(issue.id);
                        const lastPokedTime = pokeTimestamps.get(issue.id);
                        return <motion.div key={issue.id} initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors bg-white" data-id="element-825" data-mp="385y71xyg17o9">
                                  <div className="flex items-start justify-between mb-3" data-id="element-826" data-mp="4qalb85ohyti">
                                    <div className="flex-1" data-id="element-827" data-mp="3vwbybm97f2hs">
                                      <div className="flex items-center gap-2 mb-1" data-id="element-828" data-mp="3vy59neuriaw4">
                                        <h4 className="font-semibold text-gray-900" data-id="element-829" data-mp="eogpxxivdj22">
                                          {issue.title}
                                        </h4>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${issue.priority === 'high' ? 'bg-red-100 text-red-700' : issue.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-830" data-mp="tbyumvwfmdoh">
                                          {issue.priority}
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-2" data-id="element-831" data-mp="eeetk02np2nl">
                                        {issue.description}
                                      </p>
                                      <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-832" data-mp="5kjloka5gy49">
                                        <span className="font-medium" data-id="element-833" data-mp="3mswvdk207lyr">
                                          {issue.contractor}
                                        </span>
                                        <span data-id="element-834" data-mp="38negoayiruti">·</span>
                                        <span data-id="element-835" data-mp="38p7nbq94uf1i">
                                          {new Date(issue.date).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-836" data-mp="3rm0hdoq4jjww">
                                    <div className="flex items-center gap-4" data-id="element-837" data-mp="3mo7fv8zrne2y">
                                      <div className="flex items-center gap-2 text-sm" data-id="element-838" data-mp="3hn1md8ya7msd">
                                        <User size={16} className="text-gray-400" data-id="element-839" data-mp="3co87bc3cjnq3" />
                                        <span className="font-medium text-gray-900" data-id="element-840" data-mp="219ba3ex0ds7n">
                                          {issue.contactName}
                                        </span>
                                        <span className="text-gray-500" data-id="element-841" data-mp="lrwx66jc80ej">·</span>
                                        <span className="text-gray-600 text-xs" data-id="element-842" data-mp="qpyealx7dpnd">
                                          {issue.contactRole}
                                        </span>
                                      </div>
                                      <a href={`tel:${issue.contactPhone}`} className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 transition-colors" data-id="element-843" data-mp="10fi2m73oqbpm">
                                        <Phone size={16} data-id="element-844" data-mp="1efapfrc3c7sb" />
                                        {issue.contactPhone}
                                      </a>
                                    </div>

                                    <div className="flex flex-col items-end gap-1" data-id="element-845" data-mp="3g7fn7jg9tzay">
                                      <button onClick={() => handlePoke(issue.id, issue.contactName)} disabled={isPoked} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isPoked ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-846" data-mp="1ly0mcjdpuaaq">
                                        <Bell size={14} data-id="element-847" data-mp="17yiyq7c5p32y" />
                                        {isPoked ? 'Poked!' : 'Poke'}
                                      </button>
                                      {lastPokedTime && <div className="flex items-center gap-1 text-xs text-gray-500" data-id="element-848" data-mp="2jgtqwrotw5it">
                                          <Clock size={12} data-id="element-849" data-mp="2r0bag522ovfd" />
                                          <span data-id="element-850" data-mp="2vy4i72j0xxhr">
                                            {getRelativeTimeString(lastPokedTime)}
                                          </span>
                                        </div>}
                                    </div>
                                  </div>
                                </motion.div>;
                      })}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* Toast Notification */}
      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-851" data-mp="1b11bux59pga" />
    </>;
}